import {
    authenticate,
    getCurrentUser,
    refreshToken,
    requestPasswordReset,
    resetPassword,
    revokeToken,
    signUpUser
} from '../api'
import {AppError, Token, User, UserConfirmationRequest} from '../models'


export const TOKEN_STORAGE_KEY = "marksman_user_token"
export const USER_STORAGE_KEY = "marksman_user_object"
export const FIRST_LOGIN_KEY = "marksman_first_login"
export const CURRENT_TEAM_KEY = "marksman_current_team"
export const STAY_LOGGED_IN_KEY = "marksman_stay_logged_in"


export let setRefreshTokenTimer = (timer, secondsUntilShouldRefresh, updateFunction) => {
    clearInterval(timer)
    if (secondsUntilShouldRefresh > 0) {
        // setInterval takes time in milliseconds
        return setInterval(updateFunction, (secondsUntilShouldRefresh * 1000))
    }
    return null
}


export let getStorage = () => {
    if (localStorage.getItem(STAY_LOGGED_IN_KEY)) {
        return localStorage
    }
    return sessionStorage
}


export let setFirstLogInFlag = () => {
    sessionStorage.setItem(FIRST_LOGIN_KEY, true)
}


export let isFirstLogIn = () => {
    return sessionStorage.getItem(FIRST_LOGIN_KEY, true)
}


export let clearFirstLogInFlag = () => {
    sessionStorage.removeItem(FIRST_LOGIN_KEY)
}


export let signUp = async (firstName, lastName, password, userID, signUpCode, history) => {
    try {
        let user = await signUpUser(firstName, lastName, password, userID, signUpCode)
        localStorage.clear()
        sessionStorage.clear()
        setFirstLogInFlag()
        authRedirect("/", history)
        return {user}
    } catch (error) {
        if (String(error).startsWith("TypeError: NetworkError")) {
            return {error: new AppError("Failed to reach server", "There was an issue with the server. Please try again in a few minutes.")}
        }
        return {error: new AppError("Failed to Confirm Account", error.message)}
    }
}


export let authRedirect = (redirectTo, history) => {
    if (redirectTo) {
        history.push(redirectTo)
    } else {
        history.push('/')
    }
}


export let updateToken = async (token) => {
    if (!token || !token.refreshToken) {
        throw new Error('Could not update token with no refresh token.')
    }

    let newToken = await refreshToken(token)
    getStorage().setItem(TOKEN_STORAGE_KEY, JSON.stringify(newToken))
    return newToken
}

export let clearToken = async () => {
    await localStorage.removeItem(TOKEN_STORAGE_KEY)
    await sessionStorage.removeItem(TOKEN_STORAGE_KEY)
}


export let loadToken = async (updateTokenHandler=()=>{}, clearTokenHandler=()=>{}) => {
    let token_from_storage = (sessionStorage.getItem(TOKEN_STORAGE_KEY) || localStorage.getItem(TOKEN_STORAGE_KEY))
    if (token_from_storage) {
        let json_dict = JSON.parse(token_from_storage)
        let token = new Token(json_dict.accessToken, json_dict.refreshToken, 0, json_dict.expiration)
        if (token.isExpired) {
            try {
                let newToken = await updateTokenHandler(token)
                return newToken
            } catch (error) {
                // token is likely revoked or wrong
                console.log(error)
                await clearTokenHandler(token)
                return null
            }
        }
        return token
    }
    return null
}


export let loadUser = async (token, use_cached_if_available = true) => {
    // If we arent authed, then no need to try and get user
    if (!token) {
        return
    }

    // load a what we can from the session quickly, if we need to, then we hit the api
    let userFromStorage = getStorage().getItem(USER_STORAGE_KEY)
    let user = null
    let currentTeam = getCurrentTeam()

    if (use_cached_if_available && userFromStorage) {
        user = User.fromLocalStorageString(userFromStorage)
    } else {
        user = await getCurrentUser(token)
        getStorage().setItem(USER_STORAGE_KEY, JSON.stringify(user))
    }

    if (!currentTeam && user.activeTeam) {
        setCurrentTeam(user.activeTeam)
    } else if (currentTeam && user.teams && user.teams.includes(currentTeam)) {
        user.activeTeam = currentTeam
    }

    return user
}


export let getCurrentTeam = () => {
    let team = localStorage.getItem(CURRENT_TEAM_KEY)
    if (!team) {
        return team
    } else {
        return parseInt(team)
    }
}


export let setCurrentTeam = async (teamID) => {
    localStorage.setItem(CURRENT_TEAM_KEY, teamID)
}


export let logIn = async (username, password, stayLoggedIn, redirectTo = '/', history) => {
    try {
        let token = await authenticate(username, password)
        localStorage.setItem(STAY_LOGGED_IN_KEY, stayLoggedIn)
        getStorage().setItem(TOKEN_STORAGE_KEY, JSON.stringify(token))
        authRedirect(redirectTo, history)
        return {token}
    } catch (error) {
        if (String(error).startsWith("TypeError: NetworkError")) {
            return {error: new AppError("Failed to reach server", "There was an issue with the server. Please try again in a few minutes.")}
        } else if (String(error.message).includes("is missing password")) {
            return {error: new AppError("Failed to Log In", "Please enter a password and try again.")}
        } else if (String(error.message).includes("is missing username")) {
            return {error: new AppError("Failed to Log In", "Please enter an email address and try again.")}
        } else if (String(error.message).includes("Too many")) {
            return {error: new AppError("Failed to Log In", "Too many failed login attempts. Try again later")}
        } else if (String(error.message).includes("Failed to fetch")) {
            return {error: new AppError("Failed to Log In", "Could not communicate with server. Please try again in a few minutes.")}
        }
        return {error: new AppError("Failed to Log In", error.message)}
    }
}


export let logOut = async (token, refreshTokenTimer, history) => {
    try {
        if (token) {
            await revokeToken(token)
        }
    } catch (error) {
        console.log(error)
    }
    clearInterval(refreshTokenTimer)
    getStorage().removeItem(TOKEN_STORAGE_KEY)
    sessionStorage.removeItem(USER_STORAGE_KEY)
    const lastTeam = localStorage.getItem(CURRENT_TEAM_KEY)
    localStorage.clear()
    sessionStorage.clear()
    if (lastTeam) {
        localStorage.setItem(CURRENT_TEAM_KEY, lastTeam)
    }
    history.push('/')
}


export let getPasswordReset = async (email, history) => {
    try {
        await requestPasswordReset(email)
        let confirmationRequest = new UserConfirmationRequest("Password Reset Requested",
            `A password reset email will be sent to <strong>${email}</strong>.`,
            (() => history.push("/login")),
            (() => history.push("/login")))
        return ({confirmationRequest})
    } catch (e) {
        console.log(e)
        let message = "We were unable to find an account with that email address. Please check the email address and try again."
        if (e.message) {
            try {
                let parsedErr = JSON.parse(e.message)
                message = parsedErr.detail || message
            } catch (e) {
                console.log(e)
            }
        }

        let error = new AppError("Failed to Reset Password",
            message)
        return ({error})
    }
}

export let setNewPassword = async (userID, oneTimeLink, password, history) => {
    try {
        let result = await resetPassword(userID, oneTimeLink, password)
        history.push("/login/?pwr=true")
        return {detail:'success'}
    } catch (e) {
        console.log(e)
        let message = "We were unable to reset your password. Please try again."
        if (e.message) {
            try {
                let parsedErr = JSON.parse(e.message)
                message = parsedErr.detail || message
            } catch (e) {
                console.log(e)
            }
        }

        let error = new AppError("Failed to Reset Password",
            message)
        return ({error})
    }
}


export let isAuthenticated = (token = null) => {
    if (!token) {
        token = JSON.parse(getStorage().getItem(TOKEN_STORAGE_KEY))
    }
    return token !== null && token.accessToken !== null && !token.isExpired
}

export const TEAM_PERMISSIONS = {
    STATUS_VIEW: 0b0001,
    VIEW_DATA: 0b0010,
    CONTROL_DEVICES: 0b0100,
    MANAGE_TEAM: 0b1000
}



// WEBPACK FOOTER //
// ./src/dataHelpers/authenticationHelpers.js